/* AP customization: tickets 43137 + 43138 — collection viewport + product carousel slide */
import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

const PLAY_THRESHOLD = 0.35;
const MAX_PLAYING = 3;
const HOVER_MEDIA = '(hover: hover) and (pointer: fine)';

/**
 * @typedef {Object} ApCardVideoRefs
 * @property {HTMLVideoElement} video
 */

/** @type {Set<ApCardVideo>} */
const registry = new Set();

/** @type {WeakMap<Element, ApCardVideo>} */
const observedToInstance = new WeakMap();

/** @type {IntersectionObserver | null} */
let sharedObserver = null;

/** @type {MediaQueryList | null} */
let motionQuery = null;

/** @type {MediaQueryList | null} */
let hoverQuery = null;

/**
 * @returns {boolean}
 */
const prefersReducedMotion = () => {
  if (!motionQuery) {
    motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    motionQuery.addEventListener('change', handleInputModeChange);
  }

  return motionQuery.matches;
};

/**
 * Hover-capable pointer (mouse / trackpad). Width is not used as the primary
 * “mobile” signal so a touch laptop at desktop width can still viewport-play.
 * @returns {MediaQueryList}
 */
const getHoverQuery = () => {
  if (!hoverQuery) {
    hoverQuery = window.matchMedia(HOVER_MEDIA);
    hoverQuery.addEventListener('change', handleInputModeChange);
  }

  return hoverQuery;
};

/**
 * @returns {boolean}
 */
const canHover = () => getHoverQuery().matches;

const handleInputModeChange = () => {
  for (const instance of registry) {
    instance.syncInputMode();
  }

  reconcile();
};

/**
 * @returns {IntersectionObserver}
 */
const getObserver = () => {
  if (sharedObserver) return sharedObserver;

  sharedObserver = new IntersectionObserver(handleIntersections, {
    threshold: [0, PLAY_THRESHOLD],
  });

  return sharedObserver;
};

/**
 * @param {IntersectionObserverEntry[]} entries
 */
const handleIntersections = (entries) => {
  for (const entry of entries) {
    const instance = observedToInstance.get(entry.target);
    if (!instance) continue;

    instance.syncIntersection(entry.intersectionRatio);
  }

  reconcile();
};

const reconcile = () => {
  if (prefersReducedMotion()) {
    for (const instance of registry) {
      instance.stopPlayback(true);
    }

    return;
  }

  /** @type {ApCardVideo[]} */
  const candidates = [];

  for (const instance of registry) {
    if (instance.isPlayCandidate()) candidates.push(instance);
  }

  candidates.sort((a, b) => b.intersectionRatio - a.intersectionRatio);

  /** @type {Set<ApCardVideo>} */
  const winners = new Set(candidates.slice(0, MAX_PLAYING));

  for (const instance of registry) {
    if (winners.has(instance)) {
      instance.startPlayback();
      continue;
    }

    instance.stopPlayback(instance.intersectionRatio === 0 && !instance.isSlideSelected);
  }
};

/**
 * Muted looping card video. Collection cards use viewport mode. Product cards
 * are a genuine carousel slide (ticket 43138): play follows the real
 * `slideshow:select` event (desktop hover-to-slide-2 and mobile swipe), not a
 * separate hover/drag state, so the video scrolls with the carousel instead
 * of floating above it. Image stays visible until the `playing` event.
 * Product hover mode covers the video slide with an opaque loader from first
 * paint until `playing` (slide 2 reuses the featured image as its poster, so
 * a translucent overlay still looks like a repeat).
 *
 * @extends {Component<ApCardVideoRefs>}
 */
class ApCardVideo extends Component {
  requiredRefs = ['video'];

  /** @type {number} */
  #intersectionRatio = 0;

  /** @type {number} */
  #playRequestId = 0;

  /** @type {boolean} */
  #shouldBePlaying = false;

  /** @type {Element | null} */
  #observed = null;

  /** @type {AbortController | null} */
  #slideAbort = null;

  /** @type {boolean} */
  #slideSelected = false;

  /** @type {Element | null} */
  #poster = null;

  get intersectionRatio() {
    return this.#intersectionRatio;
  }

  get isSlideSelected() {
    return this.#slideSelected;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#poster = this.#findPoster();
    this.#poster?.classList.add('ap-card-media');

    registry.add(this);

    const video = this.#videoEl();
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.addEventListener('playing', this.#onPlaying);
    }

    if (this.#usesHover()) this.#setLoading(true);

    this.#attachForCurrentMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#poster?.classList.remove('ap-card-media', 'ap-card-media--playing', 'ap-card-media--loading');
    this.#poster = null;

    const video = this.#videoEl();
    video?.removeEventListener('playing', this.#onPlaying);

    this.#unbindSlide();
    this.#unobserveViewport();

    registry.delete(this);
    this.stopPlayback(true);

    if (registry.size === 0) {
      sharedObserver?.disconnect();
      sharedObserver = null;
      return;
    }

    reconcile();
  }

  /**
   * Rebind viewport observation when the pointer capability changes (affects
   * `#playOnThisInput()` for hover-mode slides and the mobile show/hide toggle).
   */
  syncInputMode() {
    this.#unbindSlide();
    this.#unobserveViewport();
    this.stopPlayback(true);
    this.#attachForCurrentMode();
  }

  /**
   * @param {number} ratio
   */
  syncIntersection(ratio) {
    this.#intersectionRatio = ratio;
  }

  /**
   * @returns {boolean}
   */
  isPlayCandidate() {
    if (!this.isConnected) return false;
    if (prefersReducedMotion()) return false;
    if (!this.#sourceUrl()) return false;

    if (this.#usesHover()) {
      return this.#slideSelected && this.#playOnThisInput() && this.#intersectionRatio >= PLAY_THRESHOLD;
    }

    if (this.#usesViewport()) return this.#intersectionRatio >= PLAY_THRESHOLD;

    return false;
  }

  startPlayback() {
    if (this.#shouldBePlaying) return;

    this.#shouldBePlaying = true;
    this.#setLoading(true);
    this.#play();
  }

  /**
   * Hide the video first so dropping `src` cannot blank the card.
   * @param {boolean} dropSrc
   */
  stopPlayback(dropSrc) {
    this.#shouldBePlaying = false;
    this.#playRequestId += 1;
    this.#setPlaying(false);

    // Product slides keep the opaque veil while idle so a swipe back to slide 2
    // is never a recognizable still. Collection viewport has no loader.
    if (this.#usesHover()) {
      this.#setLoading(true);
    } else {
      this.#setLoading(false);
    }

    const video = this.#videoEl();
    if (!video) return;

    video.pause();

    if (!dropSrc) return;
    if (!video.getAttribute('src')) return;

    video.preload = 'none';
    video.removeAttribute('src');
    video.load();
  }

  #attachForCurrentMode() {
    this.#syncVideoSlideVisibility();

    if (prefersReducedMotion()) return;

    if (this.#usesHover()) {
      this.#bindSlide();
      this.#observeViewport();
      return;
    }

    if (this.#usesViewport()) {
      this.#observeViewport();
    }
  }

  /**
   * Product cards. The video slide plays whenever it is the genuine current
   * slide (desktop hover-to-slide-2 via Horizon's own `previewImage`, or
   * mobile swipe) — both go through the real `slideshow:select` event.
   * @returns {boolean}
   */
  #usesHover() {
    return this.dataset.mode === 'hover';
  }

  /**
   * Collection cards only. Product hover mode must not viewport-autoplay.
   * @returns {boolean}
   */
  #usesViewport() {
    return this.dataset.mode === 'viewport';
  }

  /**
   * Touch devices honor `product_card_video_on_mobile`. Desktop always can play.
   * @returns {boolean}
   */
  #playOnThisInput() {
    return canHover() || this.#playOnMobile();
  }

  /**
   * @returns {boolean}
   */
  #playOnMobile() {
    return this.dataset.playOnMobile !== 'false';
  }

  #observeViewport() {
    const root = this.#observeRoot();
    this.#observed = root;
    observedToInstance.set(root, this);
    getObserver().observe(root);
  }

  #unobserveViewport() {
    if (!this.#observed) return;

    sharedObserver?.unobserve(this.#observed);
    observedToInstance.delete(this.#observed);
    this.#observed = null;
    this.#intersectionRatio = 0;
  }

  /**
   * Keep the injected video slide in the carousel on desktop. Hide it on touch
   * when the theme setting is off, and on reduced motion, so Horizon
   * `slideshow.slides` does not count it. Enable this card’s 2-slide carousel
   * when the global product-card carousel is off (desktop included).
   */
  #syncVideoSlideVisibility() {
    if (this.dataset.mode !== 'hover') return;

    const slide = this.#videoSlideEl();
    if (!slide) return;

    const hideSlide = prefersReducedMotion() || !this.#playOnThisInput();
    slide.toggleAttribute('hidden', hideSlide);

    const slideshow = this.#gallerySlideshow();
    if (!slideshow) return;
    if (!slideshow.hasAttribute('data-ap-card-video-carousel')) return;

    if (hideSlide) {
      slideshow.setAttribute('disabled', 'true');
      return;
    }

    slideshow.removeAttribute('disabled');
  }

  /**
   * Listens for the real `slideshow:select` event — the same event Horizon
   * dispatches for hover-to-slide-2 (via `previewImage`), drag, scroll, and
   * arrow navigation — so the video plays only while its slide is genuinely
   * the current one, no matter how the user got there.
   */
  #bindSlide() {
    const slideshow = this.#gallerySlideshow();
    if (!slideshow) return;

    this.#syncSlideSelected();
    this.#slideAbort = new AbortController();
    const { signal } = this.#slideAbort;
    slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect, { signal });
    // Warm the file on swipe/press so fetch overlaps the gesture. Does not
    // play — play still follows `slideshow:select` only.
    slideshow.addEventListener('pointerdown', this.#onSlideshowPointerDown, {
      signal,
      passive: true,
    });
  }

  #unbindSlide() {
    this.#slideAbort?.abort();
    this.#slideAbort = null;
    this.#slideSelected = false;
  }

  #syncSlideSelected() {
    const slide = this.#videoSlideEl();
    this.#slideSelected = slide?.getAttribute('aria-hidden') === 'false';
  }

  /**
   * @param {Event} event
   */
  #onSlideshowSelect = (event) => {
    if (!(event instanceof SlideshowSelectEvent)) return;

    this.#slideSelected = event.detail.slide === this.#videoSlideEl();

    reconcile();
  };

  /**
   * Start fetching the MP4 when the shopper first touches the carousel,
   * before slide 2 is selected. Playback still waits for `slideshow:select`.
   * The opaque loader is already on the host from first paint; this keeps it
   * on if a previous `playing` state was cleared.
   */
  #onSlideshowPointerDown = () => {
    if (prefersReducedMotion()) return;
    if (!this.#playOnThisInput()) return;

    this.#ensureSrc();
    if (!this.classList.contains('ap-card-video--playing')) this.#setLoading(true);
  };

  /**
   * @returns {HTMLElement | null}
   */
  #gallerySlideshow() {
    const gallery = this.closest('.card-gallery');
    const slideshow = gallery?.querySelector('slideshow-component');
    if (slideshow instanceof HTMLElement) return slideshow;

    return null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #videoSlideEl() {
    const gallery = this.closest('.card-gallery');
    const slide = gallery?.querySelector('.ap-card-video-slide');
    if (slide instanceof HTMLElement) return slide;

    return null;
  }

  /**
   * @returns {Element}
   */
  #observeRoot() {
    return (
      this.closest('.collection-card') ||
      this.closest('product-card') ||
      this.closest('.card-gallery') ||
      this
    );
  }

  /**
   * The still poster is always the preceding `product-media`/`resource-image`
   * sibling — collection cards render it before this host, and the product
   * card video slide renders its poster before this host too (both siblings
   * inside the same slide container).
   * @returns {Element | null}
   */
  #findPoster() {
    const previous = this.previousElementSibling;
    if (previous?.classList.contains('resource-image')) return previous;
    if (previous?.classList.contains('product-media')) return previous;

    const gallery = this.closest('.card-gallery');
    if (gallery) return gallery;

    return this.parentElement?.querySelector(':scope > .resource-image') ?? null;
  }

  /**
   * @returns {HTMLVideoElement | null}
   */
  #videoEl() {
    const video = this.refs.video;
    if (!(video instanceof HTMLVideoElement)) return null;

    return video;
  }

  /**
   * @returns {string}
   */
  #sourceUrl() {
    const video = this.#videoEl();
    const src = video?.dataset.src;
    if (!src) return '';

    if (src.startsWith('//')) return `${window.location.protocol}${src}`;

    return src;
  }

  #ensureSrc() {
    const video = this.#videoEl();
    const src = this.#sourceUrl();
    if (!video || !src) return;

    // `preload="none"` in markup avoids fetching every card on page load.
    // Switch to auto once this card actually needs the file (pointerdown
    // warm or play) so setting `src` starts the download before `play()`.
    video.preload = 'auto';
    if (video.getAttribute('src') === src) return;

    video.src = src;
    video.load();
  }

  #play = async () => {
    const video = this.#videoEl();
    if (!video || !this.#shouldBePlaying) return;

    this.#ensureSrc();
    video.muted = true;
    video.playsInline = true;

    const requestId = ++this.#playRequestId;

    try {
      await video.play();
    } catch {
      // Autoplay blocked or interrupted — keep the still image visible.
      if (requestId === this.#playRequestId) this.#setLoading(false);
    }

    if (requestId !== this.#playRequestId) return;
  };

  #onPlaying = () => {
    if (!this.#shouldBePlaying) return;

    this.#setPlaying(true);
  };

  /**
   * Product cards only. Collection viewport play keeps the still until
   * `playing` with no extra loader — that image is not a repeated slide.
   * @param {boolean} loading
   */
  #setLoading(loading) {
    if (loading && !this.#usesHover()) return;
    if (loading && this.classList.contains('ap-card-video--playing')) return;

    // Buffered return swipe: do not add a spinner that would sit until the
    // next `playing` if we are already about to paint. Leave the current
    // cover as-is (restored on stop) so the still never flashes through.
    if (loading && this.#shouldBePlaying) {
      const video = this.#videoEl();
      if (video && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return;
    }

    this.classList.toggle('ap-card-video--loading', loading);
    this.#poster?.classList.toggle('ap-card-media--loading', loading);
  }

  /**
   * @param {boolean} playing
   */
  #setPlaying(playing) {
    this.classList.toggle('ap-card-video--playing', playing);
    this.#poster?.classList.toggle('ap-card-media--playing', playing);

    if (playing) this.#setLoading(false);
  }
}

if (!customElements.get('ap-card-video')) {
  customElements.define('ap-card-video', ApCardVideo);
}
